import React, { useEffect, useState } from 'react'
import { withRouter } from 'react-router-dom'
import PropTypes from 'prop-types'
import cn from 'classnames'

import { centsToDollars } from '../../utils/formatters'

import VNPusher, {
  CHANNEL_PRIVATE_SOURCE_DEVICE_ID,
  PUSHER_EVENT_ORDER_TRANSFERRED,
  BIND_RICH_CHECKOUT_CONTAINER,
  PUSHER_EVENT_ORDER_CREATED,
  BIND_RICH_CHECKOUT_CONTAINER_ORDER_CREATED } from '../../pusher'

import generateUuid from 'uuid/v4'
import { isOfflineApproval } from '../../utils/offlineReplayStatuses'
import { nextPosOrderNumber } from '../../utils/orderNumbers'
import PeripheralBridge from '../../utils/peripheralBridge'
import { SIGNATURE_THRESHOLD_AMOUNT } from '../../constants'

import { ReactComponent as CloseIcon } from '../../assets/icons/richCheckout/close-button.svg'
import PayWithApp from '../../containers/richCheckout/PayWithApp'
import PayWithCard from './PayWithCard'
import RichCheckoutHeader from '../../containers/richCheckout/Header'

import { fakeCreditCardResponse, showFakeCreditCard } from '../../utils/fakeCreditCardResponse'

import styles from './Container.module.scss'

const RichCheckoutContainer = ({ clearCart, onCardReaderSuccess, onOfflineCardReaderSuccess, saveRichCheckoutOrder, total, className, history }) => {
  const classNames = cn(styles.richCheckout, className)

  const [cardHasFailed, setCardHasFailed] = useState(false)
  const [formattedTotal] = useState(centsToDollars(total || 0))
  const [isScanned, setIsScanned] = useState(false)
  const [orderNumber] = useState(nextPosOrderNumber(true))
  const [uuid] = useState(generateUuid())
  const [transactionApproved, setTransactionApproved] = useState(false)

  // credit card listener
  useEffect(() => {
    const bridgeInstance = PeripheralBridge.getBridge()
    if (!bridgeInstance) return

    bridgeInstance.registerHandler('handleResult', function(data) {
      if (data.status === 'ACCEPT') {
        setTransactionApproved(true)
        const dataWithUuid = { ...data, uuid, orderNumber }

        isOfflineApproval(data) ? onOfflineCardReaderSuccess(dataWithUuid) : onCardReaderSuccess(dataWithUuid)
        redirectToNextStep()
      } else {
        setCardHasFailed(true)
      }
    })

    getCardReaderReady()

  // TODO: We need to audit the exhaustive dependencies here
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formattedTotal, uuid])

  useEffect(() => {
    return () => {
      if (!transactionApproved) {
        shutDownCardReader()
      }
    }
  }, [transactionApproved])
  // pusher socket for rich checkout
  useEffect(() => {
    VNPusher.subscribe(CHANNEL_PRIVATE_SOURCE_DEVICE_ID)
    VNPusher.bind(BIND_RICH_CHECKOUT_CONTAINER, CHANNEL_PRIVATE_SOURCE_DEVICE_ID, PUSHER_EVENT_ORDER_TRANSFERRED, (data) => {
      setIsScanned(true)
    })

    VNPusher.bind(BIND_RICH_CHECKOUT_CONTAINER_ORDER_CREATED, CHANNEL_PRIVATE_SOURCE_DEVICE_ID, PUSHER_EVENT_ORDER_CREATED, (data) => {
      saveRichCheckoutOrder({ uuid, orderNumber })
      history.replace(`/receipt/${uuid}`)
    })

    // we'll want to use the below when the ios sdk persists the order uuid from the qr code
    // const orderChannel = socket.subscribe(`private-order-${uuid}`)
    // orderChannel.bind('order-created', (data) => {
    //   saveRichCheckoutOrder({ uuid, orderNumber })
    //   history.replace(`/rich-checkout/receipt/${uuid}`)
    // })
    return () => {
      VNPusher.unsubscribe(CHANNEL_PRIVATE_SOURCE_DEVICE_ID)
    }
  // TODO: We need to audit the exhaustive dependencies here
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formattedTotal, orderNumber, uuid])

  const getCardReaderReady = () => {
    const bridgeInstance = PeripheralBridge.getBridge()

    if (bridgeInstance) {
      bridgeInstance.call('readRawCardData', { 'uuid': uuid, 'amount': formattedTotal })
      setCardHasFailed(false)
    }
  }

  const goBack = () => {
    shutDownCardReader()
    if (isScanned) clearCart()

    return history.push('/concession-order')
  }

  const onFakeCreditCardSwipe = () => {
    onCardReaderSuccess({ ...fakeCreditCardResponse(uuid, formattedTotal), orderNumber })
    redirectToNextStep()
  }

  const redirectToNextStep = () => {
    const newRoute = total >= SIGNATURE_THRESHOLD_AMOUNT ? `/signature/${uuid}` : `/receipt/${uuid}`
    history.push(newRoute)
  }

  const shutDownCardReader = () => {
    const bridgeInstance = PeripheralBridge.getBridge()

    if (bridgeInstance) bridgeInstance.call('cancelTransactionAttempt')
  }

  return (
    <div className={classNames}>
      <div className={styles.richCheckoutContainer}>
        <CloseIcon className={styles.closeButton} onClick={goBack}/>

        <RichCheckoutHeader orderNumber={orderNumber} />

        <div className={styles.paymentOptionsContainer}>
          <div className={styles.payWithApp}>
            <PayWithApp
              isScanned={isScanned}
              orderNumber={orderNumber}
              orderUuid={uuid}
            />
          </div>

          <div className={styles.middleColumn}>- OR -</div>

          <div className={styles.payWithCard}>
            { showFakeCreditCard() && (
              <div className={styles.fakeCard} onClick={onFakeCreditCardSwipe}>
                Pretend Credit Card was swiped
              </div>
            )}

            <PayWithCard
              cardHasFailed={cardHasFailed}
              onRetryCard={getCardReaderReady}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

RichCheckoutContainer.defaultProps = {
  clearCart: () => {},
  onCardReaderSuccess: () => {},
  onOfflineCardReaderSuccess: () => {},
  saveRichCheckoutOrder: () => {},
  total: 0,
}

RichCheckoutContainer.propTypes = {
  clearCart: PropTypes.func,
  onCardReaderSuccess: PropTypes.func,
  onOfflineCardReaderSuccess: PropTypes.func,
  saveRichCheckoutOrder: PropTypes.func,
  total: PropTypes.number,
}

export default withRouter(RichCheckoutContainer)
